import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, FileText, Eye, Search, SlidersHorizontal, MoreVertical, Star, Pin } from 'lucide-react'
import clsx from 'clsx'
import axios from 'axios'
import { buildApiUrl } from '../../../config/api'
import { toast } from '../../../components/Toast'
import { Button, Badge, Card, EmptyState, SearchInput, SlidePanel, PaginationBar, DeleteConfirmModal } from '../../../components/ui'
import { formatDateTime } from '../../../utils/dateUtils'
import { inputClass } from '../../../styles/forms'

interface Post {
  id: number; title: string; slug: string; excerpt: string | null
  cover_image_url: string | null; is_featured: boolean; is_pinned: boolean
  status_code: string; status_label: string
  category_name: string | null; author_name: string | null
  published_at: string | null; views_count: number; reading_time: number | null
  created_at: string; tags: { name: string; slug: string }[]
}

interface Category { id: number; name: string }

const statusVariant: Record<string, 'success' | 'warning' | 'gray' | 'brand'> = {
  PUBLISHED: 'success', SCHEDULED: 'warning', DRAFT: 'gray', ARCHIVED: 'gray',
}

export default function BlogPostsPage() {
  const navigate = useNavigate()
  const [posts, setPosts] = useState<Post[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const [perPage, setPerPage] = useState(10)
  const [meta, setMeta] = useState({ total: 0, page: 1, per_page: 10, last_page: 1 })
  const [filterOpen, setFilterOpen] = useState(false)
  const [filters, setFilters] = useState({ status: '', category_id: '' })
  const [tempFilters, setTempFilters] = useState(filters)
  const [deleteId, setDeleteId] = useState<number | null>(null)
  const [openMenuId, setOpenMenuId] = useState<number | null>(null)

  const activeFilterCount = [filters.status, filters.category_id].filter(Boolean).length

  const fetchPosts = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { search, page, per_page: perPage }
      if (filters.status) params.status = filters.status
      if (filters.category_id) params.category_id = filters.category_id
      const res = await axios.get(buildApiUrl('/v1/posts'), { params })
      setPosts(res.data.data)
      if (res.data.meta) setMeta(res.data.meta)
    } catch { /* */ } finally { setLoading(false) }
  }, [search, page, perPage, filters])

  useEffect(() => { fetchPosts() }, [fetchPosts])
  useEffect(() => { setPage(1) }, [search, perPage, filters])

  useEffect(() => {
    axios.get(buildApiUrl('/v1/posts/categories')).then(r => setCategories(r.data.data)).catch(() => {})
  }, [])

  async function handleDelete() {
    if (!deleteId) return
    await axios.delete(buildApiUrl(`/v1/posts/${deleteId}`))
    toast('success', 'Post excluído')
    setDeleteId(null)
    fetchPosts()
  }

  async function handlePublish(id: number) {
    await axios.put(buildApiUrl(`/v1/posts/${id}/publish`))
    toast('success', 'Post publicado')
    setOpenMenuId(null)
    fetchPosts()
  }

  async function handleArchive(id: number) {
    await axios.put(buildApiUrl(`/v1/posts/${id}/archive`))
    toast('success', 'Post arquivado')
    setOpenMenuId(null)
    fetchPosts()
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Posts</h1>
          <p className="text-gray-500 mt-1">Gerencie o conteúdo do blog</p>
        </div>
        <Button icon={<Plus className="w-4 h-4" />} onClick={() => navigate('/blog/posts/new')}>Novo Post</Button>
      </div>

      <div className="flex items-center gap-3">
        <div className="flex-1">
          <SearchInput placeholder="Buscar por título..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <button
          onClick={() => { setTempFilters(filters); setFilterOpen(true) }}
          className={clsx('h-11 px-4 flex items-center gap-2 rounded-lg border text-sm font-medium transition-colors',
            activeFilterCount > 0 ? 'bg-brand-50 border-brand-200 text-brand-600' : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50'
          )}
        >
          <SlidersHorizontal className="w-4 h-4" /> Filtros
          {activeFilterCount > 0 && <span className="ml-1 bg-brand-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">{activeFilterCount}</span>}
        </button>
      </div>

      {loading ? (
        <Card padding="none">
          <div className="p-5 space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-gray-50 rounded-xl animate-pulse" />)}</div>
        </Card>
      ) : posts.length === 0 ? (
        <Card>
          <EmptyState icon={<FileText className="w-7 h-7" />} title="Nenhum post" description="Crie o primeiro post do blog."
            action={<Button size="sm" onClick={() => navigate('/blog/posts/new')} icon={<Plus className="w-4 h-4" />}>Criar post</Button>} />
        </Card>
      ) : (
        <Card padding="none">
          <div className="divide-y divide-gray-100">
            {posts.map(post => (
              <div key={post.id} className="flex items-center gap-4 px-5 py-4 hover:bg-gray-50/50 transition-colors cursor-pointer" onClick={() => navigate(`/blog/posts/${post.id}/edit`)}>
                {post.cover_image_url ? (
                  <img src={post.cover_image_url} alt="" className="w-16 h-12 rounded-lg object-cover flex-shrink-0" />
                ) : (
                  <div className="w-16 h-12 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="w-5 h-5 text-gray-300" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    <p className="text-sm font-medium text-gray-900 truncate">{post.title}</p>
                    {post.is_pinned && <Pin className="w-3 h-3 text-brand-500 flex-shrink-0" />}
                    {post.is_featured && <Star className="w-3 h-3 text-amber-500 flex-shrink-0" />}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-gray-500">
                    <Badge variant={statusVariant[post.status_code] || 'gray'} size="sm">{post.status_label}</Badge>
                    {post.category_name && <span>{post.category_name}</span>}
                    {post.author_name && <span>por {post.author_name}</span>}
                    {post.published_at && <span>{formatDateTime(post.published_at)}</span>}
                    {post.views_count > 0 && <span className="flex items-center gap-1"><Eye className="w-3 h-3" /> {post.views_count}</span>}
                  </div>
                </div>
                <div className="relative flex-shrink-0">
                  <button onClick={e => { e.stopPropagation(); setOpenMenuId(openMenuId === post.id ? null : post.id) }}
                    className="h-8 w-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100">
                    <MoreVertical className="w-4 h-4" />
                  </button>
                  {openMenuId === post.id && (<>
                    <div className="fixed inset-0 z-10" onClick={() => setOpenMenuId(null)} />
                    <div className="absolute right-0 top-9 z-20 w-44 bg-white rounded-xl shadow-lg border border-gray-200 py-1">
                      <button onClick={e => { e.stopPropagation(); navigate(`/blog/posts/${post.id}/edit`); setOpenMenuId(null) }}
                        className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Editar</button>
                      {post.status_code !== 'PUBLISHED' && (
                        <button onClick={e => { e.stopPropagation(); handlePublish(post.id) }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Publicar</button>
                      )}
                      {post.status_code === 'PUBLISHED' && (
                        <button onClick={e => { e.stopPropagation(); handleArchive(post.id) }}
                          className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">Arquivar</button>
                      )}
                      <button onClick={e => { e.stopPropagation(); setDeleteId(post.id); setOpenMenuId(null) }}
                        className="w-full text-left px-3 py-2 text-sm text-error-500 hover:bg-error-50">Excluir</button>
                    </div>
                  </>)}
                </div>
              </div>
            ))}
          </div>
          <PaginationBar page={page} perPage={perPage} total={meta.total} lastPage={meta.last_page} onPageChange={setPage} onPerPageChange={n => { setPerPage(n); setPage(1) }} />
        </Card>
      )}

      <SlidePanel open={filterOpen} onClose={() => setFilterOpen(false)} title="Filtros" size="sm">
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Status</label>
            <select value={tempFilters.status} onChange={e => setTempFilters({ ...tempFilters, status: e.target.value })} className={clsx(inputClass, 'appearance-none')}>
              <option value="">Todos</option>
              <option value="DRAFT">Rascunho</option>
              <option value="SCHEDULED">Agendado</option>
              <option value="PUBLISHED">Publicado</option>
              <option value="ARCHIVED">Arquivado</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Categoria</label>
            <select value={tempFilters.category_id} onChange={e => setTempFilters({ ...tempFilters, category_id: e.target.value })} className={clsx(inputClass, 'appearance-none')}>
              <option value="">Todas</option>
              {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
          <div className="pt-4 flex gap-3">
            <Button onClick={() => { setFilters(tempFilters); setFilterOpen(false) }} className="flex-1">Aplicar</Button>
            <Button variant="outline" onClick={() => { const empty = { status: '', category_id: '' }; setTempFilters(empty); setFilters(empty); setFilterOpen(false) }} className="flex-1">Limpar</Button>
          </div>
        </div>
      </SlidePanel>

      <DeleteConfirmModal open={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete}
        title="Excluir post" message={<>Tem certeza que deseja excluir este post? Esta ação não pode ser desfeita.</>} confirmLabel="Excluir" />
    </div>
  )
}
